"use client";

import React, { useState, useEffect } from 'react';
import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface Tip {
  id: number;
  category: string;
  title: string;
  content: string;
}

interface RotatingTipsProps {
  className?: string;
  dismissible?: boolean;
}

export default function RotatingTips({ className = "", dismissible = false }: RotatingTipsProps) {
  const [tips, setTips] = useState<Tip[]>([]);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Fetch tips from the JSON file
    const fetchTips = async () => {
      try {
        const response = await fetch('/scheduling-tips.json');
        const data = await response.json();
        setTips(data.tips || []);
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to load scheduling tips:', error);
        setIsLoading(false);
      }
    };

    fetchTips();
  }, []);

  useEffect(() => {
    if (tips.length === 0) return;

    // Rotate tips every 10 seconds
    const interval = setInterval(() => {
      setCurrentTipIndex((prevIndex) => (prevIndex + 1) % tips.length);
    }, 10000);

    return () => clearInterval(interval);
  }, [tips.length]);

  if (isLoading || tips.length === 0 || isDismissed) {
    return null;
  }

  const currentTip = tips[currentTipIndex];

  return (
    <aside
      aria-label="Scheduling tip"
      className={`rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)] p-3 text-[var(--ss-text)] shadow-e2 ${className}`}
    >
      <div className="flex items-start gap-2">
        <InformationCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--ss-text-muted)]" />
        <div className="flex-1 min-w-0">
          <h4 className="text-xs font-medium text-slate-900 dark:text-white mb-1">
            Tip: {currentTip.title}
          </h4>
          <p className="text-xs leading-relaxed text-[var(--ss-text-muted)]">
            {currentTip.content}
          </p>
        </div>
        {dismissible ? (
          <button
            type="button"
            onClick={() => setIsDismissed(true)}
            className="-mr-1 -mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-control text-[var(--ss-text-muted)] transition-colors hover:bg-[var(--ss-surface-subtle)] hover:text-[var(--ss-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] motion-reduce:transition-none"
            aria-label="Dismiss scheduling tip"
            title="Dismiss tip"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </aside>
  );
}
